import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useWineAppState } from './hooks/useWineAppState';
import logo from './assets/wineapp-logo.png';

// Import step components
import WelcomeStep from './components/steps/WelcomeStep';
import CookingStep from './components/steps/CookingStep';
import RestaurantSelectionStep from './components/steps/RestaurantSelectionStep';
import WinePreferencesStep from './components/steps/WinePreferencesStep';
import FoodPreferencesStep from './components/steps/FoodPreferencesStep';
import PriceRangeStep from './components/steps/PriceRangeStep';
import RecommendationsStep from './components/steps/RecommendationsStep';

// Import UI components
import LoadingIndicator from './components/ui/LoadingIndicator';
import { fetchRestaurantMenuData } from './utils/menuApi';

import './App.css';

// API Base URL configuration
const API_BASE_URL = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || 'http://localhost:5001';

const WineApp = ({ step }) => {
    const navigate = useNavigate();
    
    // Store the user prompt for CSV logging
    const [userPrompt, setUserPrompt] = useState(null);
    
    const {
        // Dining Location
        diningLocation,
        setDiningLocation,
        
        // Restaurant
        restaurant,
        setRestaurant,
        allRestaurants,
        setAllRestaurants,
        filteredRestaurants,
        setFilteredRestaurants,
        showSuggestions,
        setShowSuggestions,
        selectedSuggestionIndex,
        setSelectedSuggestionIndex,
        restaurantNotFound,
        setRestaurantNotFound,
        
        // Wine Preferences
        selectedWinePreferences,
        setSelectedWinePreferences,
        winePreferences,
        setWinePreferences,
        toggleWinePreference,
        
        // Food Preferences
        foodChoice,
        setFoodChoice,
        selectedFoodPreferences,
        toggleFoodPreference,
        
        // Price Range
        priceRange,
        setPriceRange,
        selectedPriceRangeLabel,
        setSelectedPriceRangeLabel,
        
        // Messages
        confirmationMessage,
        setConfirmationMessage,
        
        // Recommendations
        recommendations,
        setRecommendations,
        isLoadingRecommendations,
        setIsLoadingRecommendations,
        errorFetchingRecommendations,
        setErrorFetchingRecommendations,
        
        // Wine Details
        setSelectedWine,
        selectedWineInfo,
        setSelectedWineInfo,
        isLoadingWineInfo,
        setIsLoadingWineInfo,
        
        // Restaurant Summary
        restaurantSummary,
        setRestaurantSummary,
        isLoadingSummaries,
        setIsLoadingSummaries,
        errorFetchingSummaries,
        setErrorFetchingSummaries,

        // Restaurant Menu
        restaurantMenu,
        setRestaurantMenu,
        isLoadingRestaurantMenu,
        setIsLoadingRestaurantMenu,
        errorFetchingRestaurantMenu,
        setErrorFetchingRestaurantMenu,

        // App control
        resetApp
    } = useWineAppState();

    // Handler for wine preference buttons (multi-selection)
    const handleWinePreferenceButtonClick = (preferenceLabel) => {
        const currentSelected = selectedWinePreferences || [];
        if (currentSelected.includes(preferenceLabel)) {
            // Remove if already selected
            setSelectedWinePreferences(currentSelected.filter(label => label !== preferenceLabel));
        } else {
            // Add if not selected
            setSelectedWinePreferences([...currentSelected, preferenceLabel]);
        }
    };

    // Handler for price range buttons (single selection)
    const handlePriceRangeButtonClick = (priceLabel) => {
        const currentSelected = selectedPriceRangeLabel || '';
        if (currentSelected === priceLabel) {
            setSelectedPriceRangeLabel(''); // Deselect if clicked again
        } else {
            setSelectedPriceRangeLabel(priceLabel); // Select new label
        }
    };

    const handleWinePreferenceChange = (e) => {
        setWinePreferences(e.target.value);
    };

    const handleFoodChoiceChange = (e) => {
        setFoodChoice(e.target.value);
    };

    const handleEnter = (e) => {
        if (e.key === 'Enter') {
            handleNext();
        }
    };

    // Restaurant handlers
    const handleRestaurantInputChange = (e) => {
        const value = e.target.value;
        setRestaurant(value);
        
        console.log('Restaurant input change:', { value, allRestaurants }); // Debug log
        
        if (value.trim() === '') {
            setShowSuggestions(false);
            setFilteredRestaurants([]);
            setRestaurantNotFound(false);
            return;
        }

        const filtered = allRestaurants.filter(restaurantName =>
            restaurantName.toLowerCase().includes(value.toLowerCase())
        );
        
        console.log('Filtered restaurants:', filtered); // Debug log
        
        setFilteredRestaurants(filtered);
        setShowSuggestions(filtered.length > 0);
        setRestaurantNotFound(filtered.length === 0 && value.trim() !== '');
        setSelectedSuggestionIndex(-1);
    };

    const handleRestaurantKeyDown = (e) => {
        if (!showSuggestions || filteredRestaurants.length === 0) {
            if (e.key === 'Enter') {
                handleNext();
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedSuggestionIndex(prev => 
                    prev < filteredRestaurants.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (selectedSuggestionIndex >= 0) {
                    const selectedRestaurant = filteredRestaurants[selectedSuggestionIndex];
                    setRestaurant(selectedRestaurant.name);
                    setShowSuggestions(false);
                    setRestaurantNotFound(false);
                } else {
                    handleNext();
                }
                break;
            case 'Escape':
                setShowSuggestions(false);
                setSelectedSuggestionIndex(-1);
                break;
            default:
                break;
        }
    };

    const handleSuggestionClick = (restaurantName) => {
        setRestaurant(restaurantName);
        setShowSuggestions(false);
        setRestaurantNotFound(false);
        setSelectedSuggestionIndex(-1);
    };

    // Restaurant search and filter logic
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setConfirmationMessage('');
        }, 10000);
        return () => clearTimeout(timeoutId);
    }, [confirmationMessage, setConfirmationMessage]);

    useEffect(() => {
        const fetchRestaurants = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/restaurants`);
                if (response.ok) {
                    const data = await response.json();
                    console.log('Fetched restaurants:', data); // Debug log
                    setAllRestaurants(data || []); // API returns array directly, not data.restaurants
                } else {
                    console.error('Failed to fetch restaurants');
                }
            } catch (error) {
                console.error('Error fetching restaurants:', error);
            }
        };

        // Only fetch restaurants if we're in a dining scenario that needs them
        if (diningLocation === 'eating_out' || step === 'restaurant') {
            fetchRestaurants();
        }
    }, [diningLocation, step, setAllRestaurants]);

    // Fetch recommendations based on preferences
    const fetchRecommendations = useCallback(async () => {
        if (step !== 'recommendations') return;
        
        // Don't fetch if we already have recommendations
        if (recommendations && recommendations.length > 0) {
            console.log('Recommendations already exist, skipping API call');
            return;
        }
        
        setIsLoadingRecommendations(true);
        setErrorFetchingRecommendations(false);

        try {
            // Build wine preferences
            let combinedWinePreferences = '';
            if (selectedWinePreferences && selectedWinePreferences.length > 0) {
                combinedWinePreferences += `I prefer wines that are: ${selectedWinePreferences.join(', ')}. `;
            }
            if (winePreferences && winePreferences.trim()) {
                combinedWinePreferences += `Additional wine preferences: ${winePreferences.trim()}`;
            }

            // Build food preferences
            let combinedFoodPreferences = '';
            if (selectedFoodPreferences && selectedFoodPreferences.length > 0) {
                combinedFoodPreferences += `Food types I prefer: ${selectedFoodPreferences.join(', ')}. `;
            }
            if (foodChoice && foodChoice.trim()) {
                combinedFoodPreferences += `Additional food details: ${foodChoice.trim()}`;
            }

            // Prepare request data based on dining location
            let requestData;
            let endpoint;
            
            if (diningLocation === 'eating_out') {
                // Restaurant recommendations
                requestData = {
                    restaurant: restaurant || '',
                    wine_preferences: combinedWinePreferences,
                    food_pairing: combinedFoodPreferences,
                    price_range: selectedPriceRangeLabel || priceRange || ''
                };
                endpoint = `${API_BASE_URL}/api/recommendations`;
            } else {
                // General/cooking recommendations
                requestData = {
                    food_preferences: combinedFoodPreferences,
                    wine_preferences: combinedWinePreferences,
                    price_range: selectedPriceRangeLabel || priceRange || ''
                };
                endpoint = `${API_BASE_URL}/api/general-recommendations`;
            }
            
            // Store the user prompt for CSV logging
            const promptData = {
                dining_location: diningLocation,
                wine_preferences: combinedWinePreferences,
                food_preferences: combinedFoodPreferences,
                price_range: selectedPriceRangeLabel || priceRange || '',
                restaurant: restaurant || '',
                timestamp: new Date().toISOString()
            };
            
            console.log('Setting userPrompt:', promptData);
            setUserPrompt(promptData);
            
            console.log('Making request to:', endpoint, 'with data:', requestData); // Debug log

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestData)
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Recommendations API response:', data); // Debug log
                
                // Handle different response structures
                if (Array.isArray(data)) {
                    setRecommendations(data);
                } else if (data.recommendations && Array.isArray(data.recommendations)) {
                    setRecommendations(data.recommendations);
                } else if (data.error) {
                    console.error('API returned error:', data.error);
                    setErrorFetchingRecommendations(true);
                } else {
                    console.log('Unexpected response structure:', data);
                    setRecommendations([]);
                }
            } else {
                console.error('Response not ok:', response.status, response.statusText);
                setErrorFetchingRecommendations(true);
            }
        } catch (error) {
            console.error('Error fetching recommendations:', error);
            setErrorFetchingRecommendations(true);
        } finally {
            setIsLoadingRecommendations(false);
        }
    }, [
        diningLocation,
        foodChoice,
        priceRange,
        recommendations,
        restaurant,
        selectedFoodPreferences,
        selectedPriceRangeLabel,
        selectedWinePreferences,
        setErrorFetchingRecommendations,
        setIsLoadingRecommendations,
        setRecommendations,
        setUserPrompt,
        step,
        winePreferences
    ]);

    useEffect(() => {
        fetchRecommendations();
    }, [fetchRecommendations]);

    // Fetch restaurant summary when we have a restaurant and are on recommendations step
    useEffect(() => {
        const fetchRestaurantSummary = async () => {
            if (step !== 'recommendations' || !restaurant || diningLocation !== 'eating_out') {
                return;
            }
            
            // Don't fetch if we already have a summary for this restaurant
            if (restaurantSummary) {
                return;
            }
            
            setIsLoadingSummaries(true);
            setErrorFetchingSummaries(false);
            
            try {
                const response = await fetch(`${API_BASE_URL}/api/restaurant-summary?restaurant=${encodeURIComponent(restaurant)}`);
                
                if (response.ok) {
                    const data = await response.json();
                    console.log('Restaurant summary response:', data); // Debug log
                    
                    if (data.restaurant_summary) {
                        setRestaurantSummary(data.restaurant_summary);
                    } else if (data.error) {
                        console.error('Restaurant summary API error:', data.error);
                        setErrorFetchingSummaries(true);
                    }
                } else {
                    console.error('Failed to fetch restaurant summary:', response.status);
                    setErrorFetchingSummaries(true);
                }
            } catch (error) {
                console.error('Error fetching restaurant summary:', error);
                setErrorFetchingSummaries(true);
            } finally {
                setIsLoadingSummaries(false);
            }
        };
        
        fetchRestaurantSummary();
    }, [
        diningLocation,
        restaurant,
        restaurantSummary,
        setErrorFetchingSummaries,
        setIsLoadingSummaries,
        setRestaurantSummary,
        step
    ]);

    // Reset cached menu when switching restaurants
    useEffect(() => {
        setErrorFetchingRestaurantMenu(null);

        if (!restaurantMenu) {
            return;
        }

        if (restaurantMenu.restaurant !== restaurant) {
            setRestaurantMenu(null);
        }
    }, [restaurant, restaurantMenu, setRestaurantMenu, setErrorFetchingRestaurantMenu]);

    const loadMenuForRestaurant = useCallback(async () => {
        if (diningLocation !== 'eating_out' || !restaurant) {
            return;
        }

        if (restaurantMenu && restaurantMenu.restaurant === restaurant) {
            setErrorFetchingRestaurantMenu(null);
            return;
        }

        setIsLoadingRestaurantMenu(true);
        setErrorFetchingRestaurantMenu(null);

        if (!restaurantMenu || restaurantMenu.restaurant !== restaurant) {
            setRestaurantMenu(null);
        }

        try {
            const data = await fetchRestaurantMenuData(restaurant);
            setRestaurantMenu(data);
        } catch (error) {
            console.error('Error fetching restaurant menu:', error);
            const message = error?.message || 'Error loading menu. Please try again.';
            setErrorFetchingRestaurantMenu(message);
        } finally {
            setIsLoadingRestaurantMenu(false);
        }
    }, [diningLocation, restaurant, restaurantMenu, setErrorFetchingRestaurantMenu, setIsLoadingRestaurantMenu, setRestaurantMenu]);

    // Route-based navigation functions
    const handleNext = () => {
        const stepRoutes = {
            'welcome': () => diningLocation === 'eating_out' ? '/restaurant' : '/cooking',
            'cooking': '/wine-preferences',
            'restaurant': '/wine-preferences', 
            'wine-preferences': () => diningLocation === 'eating_out' ? '/food-preferences' : '/price-range',
            'food-preferences': '/price-range',
            'price-range': '/recommendations',
            'recommendations': '/'
        };
        
        const nextRoute = stepRoutes[step];
        if (typeof nextRoute === 'function') {
            navigate(nextRoute());
        } else if (nextRoute) {
            navigate(nextRoute);
        }
    };

    const handleBack = () => {
        const stepRoutes = {
            'cooking': '/welcome',
            'restaurant': '/welcome',
            'wine-preferences': () => diningLocation === 'eating_out' ? '/restaurant' : '/cooking',
            'food-preferences': '/wine-preferences',
            'price-range': () => diningLocation === 'eating_out' ? '/food-preferences' : '/wine-preferences',
            'recommendations': '/price-range'
        };
        
        const backRoute = stepRoutes[step];
        if (typeof backRoute === 'function') {
            navigate(backRoute());
        } else if (backRoute) {
            navigate(backRoute);
        }
    };

    const handleViewMenu = useCallback(() => {
        if (diningLocation !== 'eating_out' || !restaurant) {
            return;
        }

        void loadMenuForRestaurant();
        navigate('/menu');
    }, [diningLocation, restaurant, loadMenuForRestaurant, navigate]);

    // Step-specific handlers (keeping existing logic)
    const handleDiningLocationSelection = (location) => {
        setDiningLocation(location);
        if (location === 'eating_out') {
            navigate('/restaurant');
        } else {
            navigate('/cooking');
        }
    };

    const handleWineSelection = async (wine) => {
        setSelectedWine(wine);
        setSelectedWineInfo(null); // Clear previous wine info
        setIsLoadingWineInfo(true);
        
        try {
            // Check if this is an "eating in" wine style or a restaurant wine
            const isWineStyle = wine.wine_style && wine.wine_type; // Eating in recommendations have these fields
            
            let requestBody;
            if (isWineStyle) {
                // For eating in - wine style recommendations
                requestBody = {
                    wine_style: wine.wine_style || '',
                    wine_type: wine.wine_type || '',
                    grape_varieties: wine.grape_varieties || '',
                    region: wine.region || '',
                    characteristics: wine.characteristics || '',
                    restaurant: '' // Empty for eating in
                };
            } else {
                // For eating out - specific restaurant wines
                requestBody = {
                    wine_name: wine.wine_name || '',
                    producer: wine.producer || '',
                    vintage: wine.vintage || '',
                    grapes: wine.grapes || '',
                    country: wine.country || '',
                    restaurant: restaurant || ''
                };
            }

            const response = await fetch(`${API_BASE_URL}/api/wine-info`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            if (response.ok) {
                const data = await response.json();
                console.log('Wine info response:', data); // Debug log
                
                if (data.wine_information) {
                    setSelectedWineInfo(data.wine_information);
                } else if (data.wine_info) {
                    setSelectedWineInfo(data.wine_info);
                } else {
                    console.log('No wine info in response, data keys:', Object.keys(data));
                    setSelectedWineInfo('Sorry, I couldn\'t get additional information about this wine at the moment.');
                }
            } else {
                setSelectedWineInfo('Sorry, I couldn\'t get additional information about this wine at the moment.');
            }
        } catch (error) {
            console.error('Error fetching wine info:', error);
            setSelectedWineInfo('Sorry, I couldn\'t get additional information about this wine at the moment.');
        } finally {
            setIsLoadingWineInfo(false);
        }
        
        // Stay on recommendations page instead of navigating away
    };

    const handleStartOver = () => {
        resetApp(); // Reset all state to initial values
        navigate('/welcome'); // Navigate back to welcome page
    };

    // Render the appropriate step component based on route
    const renderStepContent = () => {
        switch (step) {
            case 'welcome':
                return (
                    <WelcomeStep
                        diningLocation={diningLocation}
                        onDiningLocationSelect={handleDiningLocationSelection}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                );

            case 'cooking':
                return (
                    <CookingStep
                        selectedFoodPreferences={selectedFoodPreferences}
                        foodChoice={foodChoice}
                        setFoodChoice={setFoodChoice}
                        toggleFoodPreference={toggleFoodPreference}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                );

            case 'restaurant':
                return (
                    <RestaurantSelectionStep
                        restaurant={restaurant}
                        setRestaurant={setRestaurant}
                        allRestaurants={allRestaurants}
                        filteredRestaurants={filteredRestaurants}
                        setFilteredRestaurants={setFilteredRestaurants}
                        showSuggestions={showSuggestions}
                        setShowSuggestions={setShowSuggestions}
                        selectedSuggestionIndex={selectedSuggestionIndex}
                        setSelectedSuggestionIndex={setSelectedSuggestionIndex}
                        restaurantNotFound={restaurantNotFound}
                        setRestaurantNotFound={setRestaurantNotFound}
                        handleRestaurantInputChange={handleRestaurantInputChange}
                        handleRestaurantKeyDown={handleRestaurantKeyDown}
                        handleSuggestionClick={handleSuggestionClick}
                        diningLocation={diningLocation}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                );

            case 'wine-preferences':
                return (
                    <WinePreferencesStep
                        selectedWinePreferences={selectedWinePreferences}
                        winePreferences={winePreferences}
                        setWinePreferences={setWinePreferences}
                        toggleWinePreference={toggleWinePreference}
                        handleWinePreferenceButtonClick={handleWinePreferenceButtonClick}
                        handleWinePreferenceChange={handleWinePreferenceChange}
                        handleEnter={handleEnter}
                        confirmationMessage={confirmationMessage}
                        diningLocation={diningLocation}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                );

            case 'food-preferences':
                return (
                    <FoodPreferencesStep
                        selectedFoodPreferences={selectedFoodPreferences}
                        foodChoice={foodChoice}
                        setFoodChoice={setFoodChoice}
                        toggleFoodPreference={toggleFoodPreference}
                        handleFoodChoiceChange={handleFoodChoiceChange}
                        handleEnter={handleEnter}
                        diningLocation={diningLocation}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                );

            case 'price-range':
                return (
                    <PriceRangeStep
                        priceRange={priceRange}
                        setPriceRange={setPriceRange}
                        selectedPriceRangeLabel={selectedPriceRangeLabel}
                        setSelectedPriceRangeLabel={setSelectedPriceRangeLabel}
                        handlePriceRangeButtonClick={handlePriceRangeButtonClick}
                        handleEnter={handleEnter}
                        onNext={handleNext}
                        onBack={handleBack}
                    />
                );

            case 'recommendations':
                if (isLoadingRecommendations) {
                    return <LoadingIndicator message="Getting your wine recommendations..." />;
                }
                
                return (
                    <RecommendationsStep
                        recommendations={recommendations}
                        isLoadingRecommendations={isLoadingRecommendations}
                        errorFetchingRecommendations={errorFetchingRecommendations}
                        restaurantSummary={restaurantSummary}
                        isLoadingSummaries={isLoadingSummaries}
                        errorFetchingSummaries={errorFetchingSummaries}
                        restaurant={restaurant}
                        handleWineSelection={handleWineSelection}
                        selectedWineInfo={selectedWineInfo}
                        isLoadingWineInfo={isLoadingWineInfo}
                        isEatingIn={diningLocation === 'eating_in'}
                        userPrompt={userPrompt}
                        onBack={handleBack}
                        onStartOver={handleStartOver}
                        onViewMenu={handleViewMenu}
                        isLoadingMenu={isLoadingRestaurantMenu}
                        menuError={errorFetchingRestaurantMenu}
                    />
                );

            default:
                return <div>Unknown step: {step}</div>;
        }
    };

    return (
        <div className="App">
            <div className="wine-recommendations-container">
                {renderStepContent()}
            </div>
            
            {/* Floating logo */}
            <div className="floating-logo">
                <Link to="/welcome">
                    <img src={logo} alt="WineApp Logo" className="logo-image" />
                </Link>
            </div>
            
            {/* Footer with About link */}
            <footer className="app-footer">
                <Link to="/about" className="about-link">About</Link>
            </footer>
        </div>
    );
};

export default WineApp;
